/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import * as _ from 'lodash';
import Styled from './ViewLocation.style';
import {
  Wrap,
  Button,
  Avatar,
  Popup,
  Icon,
  BackButton,
  NothingToDisplay,
  Typography,
  Loader,
} from '../../components/index';
import { Row, Col } from 'react-styled-flexboxgrid';
import { useParams, useNavigate } from 'react-router-dom';
import { useResponsivity } from '../../helpers/helpers';
import { useTranslation } from 'react-i18next';
import { getOptionLabel } from '../AddRoom/getOptionLabel';
import { washroomConfig } from '../AddRoom/Washroom/washroomJson';
import { kitchenConfig } from '../AddRoom/Kitchen/kitchenJson';

interface ViewLocationProps {
  listLocation: any;
  room: any;
  deleteRoom: Function;
  listLocations: Function;
  updateRoomUsage: Function;
}

interface RoomActions {
  leaksFixed: string[];
  postersFitted: string[];
  tapInsertsSelected: string[];
}

const ViewLocation: React.FC<ViewLocationProps> = (props) => {
  const { listLocation, room, deleteRoom, listLocations, updateRoomUsage } =
    props;

  const { locationId } = useParams();
  const [expandedRoom, setExpandedRoom] = React.useState<string | null>(null);

  const [actions, setActions] = React.useState<Record<string, RoomActions>>({});

  const [isId, setId] = React.useState();
  const [popup, setPopup] = React.useState(false);

  const data = _.get(listLocation, 'data.data');
  const isMobile = useResponsivity();
  const navigate = useNavigate();

  const { loading, done } = _.get(listLocation, 'status');

  const loadingRoomUsage = _.get(room, 'updateRoom.status.loading');
  const errorRoomUsage = _.get(room, 'updateRoom.status.error');
  const doneRoomUsage = _.get(room, 'updateRoom.status.done');
  const roomUsageMessage = _.get(room, 'updateRoom.data.message');

  const [t] = useTranslation('common');

  const findElement = _.find(data, function (o) {
    return o.id == locationId;
  });

  const getRooms = _.get(findElement, 'rooms');
  const roomLength = _.get(getRooms, 'length');

  const sadFace = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 16C16 16 14.5 14 12 14C9.5 14 8 16 8 16M9 9H9.01M15 9H15.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
        stroke="#ED1C25"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const happyFace = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14M9 9H9.01M15 9H15.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
        stroke="#4BAE50"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const toggleRoom = (roomId: string) => {
    setExpandedRoom((current) => (current === roomId ? null : roomId));
  };

  React.useEffect(() => {
    listLocations();
  }, [listLocations]);

  /*
   * Initialise the action state from values already returned by the API.
   *
   * Posters:
   * {
   *   "curtis-poster": true,
   *   "kenneth-poster": true
   * }
   *
   * Tap inserts:
   * {
   *   "tap-inserts-fitted": ["tap_0", "tap_1"]
   * }
   *
   * Supports the values being directly on the room OR inside room.data.
   */
  React.useEffect(() => {
    if (!getRooms) return;

    setActions((prev) => {
      const updated = { ...prev };

      _.forEach(getRooms, (room: any) => {
        // Don't overwrite local changes if this room already
        // has an action state.
        if (updated[room.id]) return;

        const posterKeys = [
          'curtis-poster',
          'gary-poster',
          'oscar-poster',
          'kenneth-poster',
          'phoebe-poster',
        ];

        const postersFitted = posterKeys.filter((posterKey) => {
          return room?.data?.[posterKey] === true || room?.[posterKey] === true;
        });

        const apiTapInserts =
          room?.data?.['tap-inserts-fitted'] ??
          room?.['tap-inserts-fitted'] ??
          [];

        const tapInsertsSelected = Array.isArray(apiTapInserts)
          ? apiTapInserts
          : [];

        updated[room.id] = {
          leaksFixed: [],
          postersFitted,
          tapInsertsSelected,
        };
      });

      return updated;
    });
  }, [getRooms]);

  const renderNoRooms = () => {
    return (
      <Col md={12} sm={12} xs={12}>
        <NothingToDisplay
          title="You have no room(s) added for this location."
          location={`/${locationId}/add-room`}
          subtitle="Please add a room in order to view your data."
          buttonText="Add room(s)"
        />
      </Col>
    );
  };

  const toggleLeakFixed = (roomId: string, applianceId: string) => {
    setActions((prev) => {
      const current = prev[roomId] ?? {
        leaksFixed: [],
        postersFitted: [],
        tapInsertsSelected: [],
      };

      const exists = current.leaksFixed.includes(applianceId);

      return {
        ...prev,
        [roomId]: {
          ...current,
          leaksFixed: exists
            ? current.leaksFixed.filter((id) => id !== applianceId)
            : [...current.leaksFixed, applianceId],
        },
      };
    });
  };

  const toggleTapInsert = (roomId: string, tapId: string) => {
    setActions((prev) => {
      const current = prev[roomId] ?? {
        leaksFixed: [],
        postersFitted: [],
        tapInsertsSelected: [],
      };

      const isSelected = current.tapInsertsSelected.includes(tapId);

      const updatedSelected = isSelected
        ? current.tapInsertsSelected.filter((id) => id !== tapId)
        : [...current.tapInsertsSelected, tapId];

      return {
        ...prev,
        [roomId]: {
          ...current,
          tapInsertsSelected: updatedSelected,
        },
      };
    });
  };

  const togglePoster = (roomId: string, poster: string) => {
    setActions((prev) => {
      const current = prev[roomId] ?? {
        leaksFixed: [],
        postersFitted: [],
        tapInsertsSelected: [],
      };

      const exists = current.postersFitted.includes(poster);

      return {
        ...prev,
        [roomId]: {
          ...current,
          postersFitted: exists
            ? current.postersFitted.filter((p) => p !== poster)
            : [...current.postersFitted, poster],
        },
      };
    });
  };

  const renderApplianceCards = (room: any) => {
    const groups =
      room.type === 'kitchen'
        ? [
            {
              title: 'Kitchen taps',
              data: room.data?.taps,
              fields: [
                { key: 'type', label: 'Type' },
                { key: 'flowrate', label: 'Flow rate' },
                { key: 'leak', label: 'Leak' },
              ],
            },
          ]
        : [
            {
              title: 'Taps',
              data: room.data?.taps,
              fields: [
                { key: 'type', label: 'Type' },
                { key: 'flowrate', label: 'Flow rate' },
                { key: 'leak', label: 'Leak' },
              ],
            },
            {
              title: 'Showers',
              data: room.data?.showers,
              fields: [
                { key: 'type', label: 'Type' },
                { key: 'control', label: 'Control' },
                { key: 'leak', label: 'Leak' },
              ],
            },
            {
              title: 'Toilets',
              data: room.data?.toilets,
              fields: [
                { key: 'type', label: 'Type' },
                { key: 'leak', label: 'Leak' },
              ],
            },
            {
              title: 'Urinals',
              data: room.data?.urinals,
              fields: [
                { key: 'type', label: 'Type' },
                { key: 'leak', label: 'Leak' },
              ],
            },
          ];

    return (
      <>
        {groups.map((group) => {
          if (!group.data || Object.keys(group.data).length === 0) {
            return null;
          }

          return (
            <div key={group.title} style={{ marginBottom: 32 }}>
              <Typography
                size="3"
                text={group.title}
                style={{ marginBottom: 16 }}
              />

              <Row>
                {Object.entries(group.data).map(([key, appliance]: any) => {
                  const applianceNumber = Number(key.split('_')[1]) + 1;

                  const hasLeak = !!appliance.leak;

                  const isMediumOrStrongFlow = [
                    'medium_flow_bathroom_basin_tap',
                    'strong_flow_bathroom_basin_tap',
                    'kitchen_tap_flow_rate_medium_count',
                    'kitchen_tap_flow_rate_strong_count',
                  ].includes(appliance.flowrate);

                  const leakFixed =
                    actions[room.id]?.leaksFixed.includes(key) ?? false;

                  const tapInsertSelected =
                    actions[room.id]?.tapInsertsSelected.includes(key) ?? false;

                  const showHappyFace = !hasLeak || leakFixed;

                  const applianceName =
                    {
                      Taps: 'Tap',
                      Showers: 'Shower',
                      Toilets: 'Toilet',
                      Urinals: 'Urinal',
                      'Kitchen taps': 'Tap',
                    }[group.title] ?? 'Appliance';

                  return (
                    <Col md={4} sm={6} xs={12} key={key}>
                      <Styled.SummaryItem
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          marginBottom: 16,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            size="p"
                            text={`${applianceName} ${applianceNumber}`}
                            style={{ fontWeight: 600 }}
                          />

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {showHappyFace ? happyFace : sadFace}

                            <span
                              style={{
                                marginLeft: '8px',
                                fontWeight: 500,
                                color: showHappyFace ? '#4BAE50' : '#ED1C25',
                              }}
                            >
                              {showHappyFace ? 'Done' : 'Needs attention'}
                            </span>
                          </div>
                        </div>

                        {group.fields.map((field) =>
                          appliance[field.key] ? (
                            <Typography
                              key={field.key}
                              size="p"
                              style={{
                                fontSize: '14px',
                                color: '#7a7a7a',
                              }}
                              text={`${field.label}: ${t(
                                getOptionLabel(
                                  room.type === 'washroom'
                                    ? washroomConfig
                                    : kitchenConfig,
                                  appliance[field.key],
                                ),
                              )}`}
                            />
                          ) : null,
                        )}

                        {appliance.leak && (
                          <Styled.ToggleRow>
                            <Typography size="p" text="Leak fixed?" />

                            <Styled.Toggle
                              checked={leakFixed}
                              onClick={() => toggleLeakFixed(room.id, key)}
                            />
                          </Styled.ToggleRow>
                        )}

                        {(group.title === 'Taps' ||
                          group.title === 'Kitchen taps') &&
                          isMediumOrStrongFlow && (
                            <Styled.ToggleRow>
                              <Typography size="p" text="Tap insert fitted?" />

                              <Styled.Toggle
                                checked={tapInsertSelected}
                                onClick={() => toggleTapInsert(room.id, key)}
                              />
                            </Styled.ToggleRow>
                          )}
                      </Styled.SummaryItem>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        })}
      </>
    );
  };

  const renderSiteChecklist = (room: any) => {
    const posters =
      room.type === 'kitchen'
        ? [
            {
              key: 'gary-poster',
              title: 'Gary poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/gecko.png',
            },
            {
              key: 'phoebe-poster',
              title: 'Phoebe poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/penguin.png',
            },
          ]
        : [
            {
              key: 'curtis-poster',
              title: 'Curtis poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/cheetah.png',
            },
            {
              key: 'gary-poster',
              title: 'Gary poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/gecko.png',
            },
            {
              key: 'oscar-poster',
              title: 'Oscar poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/monkey.png',
            },
            {
              key: 'kenneth-poster',
              title: 'Kenneth poster',
              image:
                'https://cdn.getwaterfit.co.uk/static/files/images/gwf-business/posters/koala.png',
            },
          ];

    return (
      <>
        <Typography size="3" text="Posters" style={{ marginBottom: 16 }} />

        <Row>
          {posters.map((poster) => {
            const isDone =
              actions[room.id]?.postersFitted.includes(poster.key) ?? false;

            return (
              <Col md={4} sm={6} xs={12} key={poster.key}>
                <Styled.SummaryItem>
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        size="p"
                        text={poster.title}
                        style={{
                          fontWeight: 600,
                          margin: 0,
                          padding: 0,
                        }}
                      />

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {isDone ? happyFace : sadFace}

                        <span
                          style={{
                            marginLeft: 8,
                            fontWeight: 500,
                            color: isDone ? '#4BAE50' : '#ED1C25',
                          }}
                        >
                          {isDone ? 'Done' : 'Needs attention'}
                        </span>
                      </div>
                    </div>

                    <img
                      src={poster.image}
                      alt={poster.title}
                      width="100px"
                      style={{ margin: '8px 0px' }}
                    />

                    <Styled.ToggleRow>
                      <Typography size="p" text="Displayed?" />

                      <Styled.Toggle
                        checked={isDone}
                        onClick={() => togglePoster(room.id, poster.key)}
                      />
                    </Styled.ToggleRow>
                  </div>
                </Styled.SummaryItem>
              </Col>
            );
          })}
        </Row>
      </>
    );
  };

  const renderRooms = () => {
    return _.map(getRooms, (el, index) => {
      return (
        <Col
          md={12}
          sm={12}
          xs={12}
          key={index}
          style={{
            marginBottom: '16px',
          }}
        >
          <Styled.Box open={expandedRoom === el.id}>
            <div
              className="top"
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <Avatar
                  style={{
                    width: '38px',
                    height: '38px',
                  }}
                >
                  {index + 1}
                </Avatar>

                <div className="title" style={{ marginLeft: '12px' }}>
                  <div>{el.name || 'Sample room title'}</div>
                  <div>{el.type}</div>
                </div>
              </div>

              <div className="bottom">
                <Button
                  onClick={() => {
                    toggleRoom(el.id);
                  }}
                  style={{
                    marginRight: '8px',
                    background: '#4BAE50',
                  }}
                  appearance="OUTLINED"
                >
                  Take action
                </Button>

                <Button
                  onClick={() => {
                    navigate(`/${locationId}/edit-room/${el.id}`);
                  }}
                  style={{ marginRight: '16px' }}
                  appearance="OUTLINED"
                >
                  <Icon icon="pen" size={20} style={{ marginRight: '8px' }} />{' '}
                  Edit
                </Button>

                <Button
                  onClick={() => {
                    setPopup(!popup);
                    setId(el.id);
                  }}
                  appearance="OUTLINED"
                >
                  <Icon icon="trash" size={20} style={{ marginRight: '8px' }} />{' '}
                  Delete
                </Button>
              </div>
            </div>

            <Styled.AccordionContent open={expandedRoom === el.id}>
              {renderApplianceCards(el)}

              {renderSiteChecklist(el)}

              <Styled.SubmitButtonWrap>
                <Button
                  disabled={loadingRoomUsage}
                  onClick={() => {
                    const payload = {
                      'leaks-fixed': actions[el.id]?.leaksFixed ?? [],

                      'tap-inserts-fitted':
                        actions[el.id]?.tapInsertsSelected ?? [],

                      'posters-fitted': actions[el.id]?.postersFitted ?? [],
                    };

                    updateRoomUsage(el.id, payload);
                  }}
                >
                  Save Progress
                </Button>
              </Styled.SubmitButtonWrap>
            </Styled.AccordionContent>
          </Styled.Box>
        </Col>
      );
    });
  };

  const renderPage = () => {
    if (roomLength === 0) {
      return renderNoRooms();
    } else {
      return renderRooms();
    }
  };

  return (
    <Wrap>
      <Row style={{ marginBottom: '16px' }}>
        <Col md={6} sm={12} xs={12}>
          <Typography size="1" text="My rooms" />

          <Typography
            size="p"
            text="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
          />
        </Col>

        <Col md={6} sm={12} xs={12}>
          <Styled.TopBtnWrap>
            <BackButton />

            {roomLength === 0 || roomLength >= 10 ? null : (
              <Button
                style={{ marginLeft: '16px' }}
                onClick={() => navigate(`/${locationId}/add-room`)}
              >
                Add new room
              </Button>
            )}
          </Styled.TopBtnWrap>
        </Col>
      </Row>

      <Row>
        {loading && !done ? (
          <Loader />
        ) : loadingRoomUsage ? (
          <Loader />
        ) : doneRoomUsage ? (
          <Col md={12} sm={12} xs={12}>
            <Typography
              size="4"
              text="Congratulations!"
              style={{
                textAlign: 'center',
                marginTop: '32px',
                fontWeight: 600,
              }}
            />

            <Typography
              size="p"
              text={roomUsageMessage}
              style={{
                textAlign: 'center',
                marginTop: '16px',
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '32px',
                gap: '16px',
              }}
            >
              <Button onClick={() => navigate('/portal')}>Home</Button>

              <Button
                appearance="OUTLINED"
                onClick={() => {
                  listLocations();
                  navigate(0);
                }}
              >
                Back to rooms
              </Button>
            </div>
          </Col>
        ) : errorRoomUsage ? (
          <Col md={12} sm={12} xs={12}>
            <Typography
              size="4"
              text="Something went wrong"
              style={{
                textAlign: 'center',
                marginTop: '32px',
                fontWeight: 600,
              }}
            />

            <Typography
              size="p"
              text={
                typeof errorRoomUsage === 'string'
                  ? errorRoomUsage
                  : 'We could not save your progress.'
              }
              style={{
                textAlign: 'center',
                marginTop: '16px',
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '32px',
              }}
            >
              <Button
                onClick={() => {
                  listLocations();
                }}
              >
                Back
              </Button>
            </div>
          </Col>
        ) : (
          renderPage()
        )}

        <Popup
          open={popup}
          isDesktop={!isMobile}
          handleClick={() => setPopup(!popup)}
        >
          <Typography
            size="3"
            text="Are you sure you want to delete your room?"
          />

          <Typography size="p" text="All your data will be lost" />

          <Styled.ButtonWrap>
            <Button
              className="delete-yes"
              onClick={() => {
                deleteRoom(isId);
                setPopup(!popup);

                setTimeout(() => {
                  listLocations();
                }, 1000);
              }}
            >
              Yes
            </Button>{' '}
            <Button>No</Button>
          </Styled.ButtonWrap>
        </Popup>
      </Row>
    </Wrap>
  );
};

export default ViewLocation;
